import { useEffect, useState, useCallback, useMemo } from "react";
import { Icon, Avatar, EmptyState } from "../../components/Common.jsx";
import { useApp } from "../../store.jsx";
import { fmtDate } from "../../lib/dates.js";
import TableSkeleton from "../../components/admin/TableSkeleton.jsx";
import AdminPageBoundary from "../../components/admin/AdminPageBoundary.jsx";
import {
  listCompanies,
  listPlans,
  suspendCompany,
  unsuspendCompany,
  deleteCompany,
  updateCompany,
  impersonateCompany,
  itemsOf,
} from "../../lib/adminApi";

const FILTERS = [
  ["all", "All"],
  ["active", "Active"],
  ["trialing", "Trialing"],
  ["suspended", "Suspended"],
  ["deleted", "Deleted"],
];

const PLAN_BADGE = {
  free_trial: "badge-warning",
  starter: "badge-info",
  growth: "badge-primary",
};

const DELETED_EMAIL_RE = /^deleted-[A-Za-z0-9]+@deleted\.local$/;
const isDeleted = (c) => c.suspendReason === "soft_deleted";
const cleanCompanyName = (name) =>
  typeof name === "string" ? name.replace(/^(\[DELETED\]\s+)+/i, "[DELETED] ") : name;
const cleanOwnerEmail = (email) =>
  email && !DELETED_EMAIL_RE.test(email) ? email : null;

const PAGE_SIZE = 25;

function SortableTH({ field, sort, dir, onChange, children, style }) {
  const active = sort === field;
  const nextDir = active && dir === "desc" ? "asc" : "desc";
  return (
    <th
      style={{ cursor: "pointer", userSelect: "none", ...style }}
      onClick={() => onChange(field, nextDir)}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {children}
        {active && (
          <Icon name={dir === "desc" ? "chevron-down" : "chevron-up"} size={12} />
        )}
      </span>
    </th>
  );
}

function CompaniesInner() {
  const { goPage, toast, openModal, closeModal } = useApp();
  const [resp, setResp] = useState({ items: [], total: 0, hasMore: false, page: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("createdAt");
  const [dir, setDir] = useState("desc");
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    listPlans().then(setPlans).catch(() => setPlans([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (planFilter) params.set("plan", planFilter);
    if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));
    params.set("sort", sort);
    params.set("dir", dir);
    try {
      const r = await listCompanies(`?${params}`);
      setResp(itemsOf(r));
    } catch (e) {
      toast(e.message || "Failed to load companies", "error");
    } finally {
      setLoading(false);
    }
  }, [search, planFilter, statusFilter, page, sort, dir, toast]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [search, planFilter, statusFilter, sort, dir]);

  const totalPages = Math.max(1, Math.ceil(resp.total / PAGE_SIZE));

  const planOptions = useMemo(() => {
    const fromDb = (plans || []).map((p) => [p.key, p.name]);
    return [["", "All plans"], ...fromDb];
  }, [plans]);

  const onEdit = (c) => {
    let draft = { name: c.name, slug: c.slug };
    let updatingName = c.name;
    let updatingSlug = c.slug;
    const save = async () => {
      if (!updatingName) return toast("Name required", "error");
      try {
        await updateCompany(c.id, { name: updatingName, slug: updatingSlug });
        toast("Company updated", "success");
        closeModal();
        load();
      } catch (e) {
        toast(e.message || "Update failed", "error");
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Edit company</h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Name</label>
            <input
              className="input"
              defaultValue={draft.name}
              onChange={(e) => { updatingName = e.target.value; }}
            />
          </div>
          <div className="field">
            <label>Slug</label>
            <input
              className="input"
              defaultValue={draft.slug}
              onChange={(e) => { updatingSlug = e.target.value; }}
            />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>
            <Icon name="check" size={14} /> Save
          </button>
        </div>
      </>,
    );
  };

  const onSuspendModal = (c) => {
    let reason = "";
    const submit = async () => {
      if (!reason || reason.trim().length < 4) return toast("Reason required (≥4 chars)", "error");
      try {
        await suspendCompany(c.id, reason);
        toast(`${c.name} suspended`, "success");
        closeModal();
        load();
      } catch (e) {
        toast(e.message || "Suspend failed", "error");
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3>Suspend {c.name}</h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Reason</label>
            <textarea
              className="input"
              rows={4}
              onChange={(e) => { reason = e.target.value; }}
              placeholder="Payment failed / TOS violation / customer request…"
            />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn" style={{ background: "var(--error)", color: "#fff" }} onClick={submit}>
            <Icon name="ban" size={14} /> Suspend
          </button>
        </div>
      </>,
    );
  };

  const onUnsuspendModal = (c) => {
    openModal(
      <>
        <div className="modal-head">
          <h3>Unsuspend {c.name}?</h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            Tenant access will be restored. All users will be able to sign in again.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={async () => {
              try {
                await unsuspendCompany(c.id);
                toast("Unsuspended", "success");
                closeModal();
                load();
              } catch (e) {
                toast(e.message || "Unsuspend failed", "error");
              }
            }}
          >
            <Icon name="play" size={14} /> Unsuspend
          </button>
        </div>
      </>,
    );
  };

  const onDelete = (c) => {
    let typed = "";
    const submit = async () => {
      if (typed !== c.slug) return toast("Slug mismatch — try again", "error");
      try {
        await deleteCompany(c.id);
        toast("Company soft-deleted", "success");
        closeModal();
        load();
      } catch (e) {
        toast(e.message || "Delete failed", "error");
      }
    };
    openModal(
      <>
        <div className="modal-head">
          <h3 style={{ color: "var(--error)" }}>
            <Icon name="alert-triangle" size={16} /> Delete {c.name}
          </h3>
          <button className="icon-btn" onClick={closeModal}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
            This is a soft delete — users will be deactivated and the company
            anonymized. Type the slug <code style={{ background: "var(--surface)", padding: "2px 6px", borderRadius: 4 }}>{c.slug}</code> to confirm.
          </p>
          <div className="field">
            <input
              className="input"
              placeholder={`Type "${c.slug}" to confirm`}
              onChange={(e) => { typed = e.target.value; }}
            />
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn" style={{ background: "var(--error)", color: "#fff" }} onClick={submit}>
            <Icon name="trash-2" size={14} /> Delete
          </button>
        </div>
      </>,
    );
  };

  const onImpersonate = (c) => {
    openModal(
      <>
        <div className="modal-head">
          <h3><Icon name="eye" size={16} /> Impersonate {cleanCompanyName(c.name)}</h3>
          <button className="icon-btn" onClick={closeModal}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13.5, color: "var(--text-muted)", lineHeight: 1.6 }}>
            You'll sign in as this company's <strong>owner</strong> and see exactly what they see. Your
            admin session stays active underneath — use the banner to exit. This action is audit-logged.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => {
            try {
              await impersonateCompany(c.id);
              closeModal();
              window.location.assign("/app");
            } catch (e) {
              toast(e.message === "user_not_found" ? "This company has no active owner to impersonate" : (e.message || "Failed"), "error");
            }
          }}>
            <Icon name="eye" size={14} /> View as owner
          </button>
        </div>
      </>,
      "sm",
    );
  };

  const onSortChange = (k, d) => { setSort(k); setDir(d); };

  const companies = resp.items;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Companies</h1>
          <p className="page-sub">{resp.total} tenants</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="toolbar" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
            <Icon
              name="search"
              size={14}
              className="input-leading"
              style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }}
            />
            <input
              className="input"
              placeholder="Search name, slug, owner email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
          {FILTERS.map(([k, l]) => (
            <button
              key={k}
              className={`filter-chip ${statusFilter === k ? "active" : ""}`}
              onClick={() => setStatusFilter(k)}
            >
              {l}
            </button>
          ))}
          <select
            className="input"
            style={{ height: 34, width: 165, fontSize: 13, padding: "0 10px" }}
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            {planOptions.map(([k, l]) => (
              <option key={k} value={k}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <TableSkeleton columns={7} rows={6} />
      ) : companies.length === 0 ? (
        <EmptyState title="No companies found" desc="Try clearing filters." icon="building" />
      ) : (
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <SortableTH field="name" sort={sort} dir={dir} onChange={onSortChange}>Company</SortableTH>
                <th>Owner</th>
                <th>Plan</th>
                <th>Users</th>
                <SortableTH field="createdAt" sort={sort} dir={dir} onChange={onSortChange}>Start Date</SortableTH>
                <SortableTH field="trialEnd" sort={sort} dir={dir} onChange={onSortChange}>Trial End</SortableTH>
                <th>Status</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => {
                const deleted = isDeleted(c);
                const displayName = cleanCompanyName(c.name);
                const ownerEmail = cleanOwnerEmail(c.users?.[0]?.email);
                return (
                <tr key={c.id} style={deleted ? { opacity: 0.7 } : undefined}>
                  <td data-label="Company">
                    <div className="cell-emp">
                      <Avatar name={displayName} size="sm" seed={displayName?.length || 0} />
                      <div>
                        <div className="name">{displayName}</div>
                        <div className="sub">{c.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Owner" style={{ fontSize: 12 }}>
                    {ownerEmail || (
                      <span style={{ color: "var(--text-muted)" }}>—</span>
                    )}
                  </td>
                  <td data-label="Plan">
                    <span className={`badge ${PLAN_BADGE[c.subscriptionPlan] || "badge-muted"}`}>
                      {c.subscriptionPlan || "free_trial"}
                    </span>
                  </td>
                  <td data-label="Users">
                    <strong>{c._count?.users ?? 0}</strong>
                  </td>
                  <td data-label="Start Date" style={{ fontSize: 12 }}>{fmtDate(c.createdAt)}</td>
                  <td data-label="Trial End" style={{ fontSize: 12 }}>
                    {c.subscription?.status === "active" ? "—" : fmtDate(c.trialEnd)}
                  </td>
                  <td data-label="Status">
                    {deleted ? (
                      <span className="badge badge-error">
                        <span className="badge-dot" /> Deleted
                      </span>
                    ) : c.suspendedAt ? (
                      <span className="badge badge-error">
                        <span className="badge-dot" /> Suspended
                      </span>
                    ) : c.isActive ? (
                      <span className="badge badge-success">
                        <span className="badge-dot" /> Active
                      </span>
                    ) : (
                      <span className="badge badge-muted">
                        <span className="badge-dot" /> Inactive
                      </span>
                    )}
                  </td>
                  <td data-label="">
                    <div className="row-actions">
                      <button
                        className="icon-btn"
                        title="View detail"
                        onClick={() => goPage("adminCompanyDetail", { companyId: c.id })}
                      >
                        <Icon name="eye" size={14} />
                      </button>
                      {!deleted && (
                        <>
                          <button className="icon-btn" title="Impersonate owner" onClick={() => onImpersonate(c)}>
                            <Icon name="eye" size={14} />
                          </button>
                          <button className="icon-btn" title="Edit" onClick={() => onEdit(c)}>
                            <Icon name="edit-2" size={14} />
                          </button>
                          {c.suspendedAt ? (
                            <button className="icon-btn" title="Unsuspend" onClick={() => onUnsuspendModal(c)}>
                              <Icon name="play" size={14} />
                            </button>
                          ) : (
                            <button className="icon-btn" title="Suspend" onClick={() => onSuspendModal(c)}>
                              <Icon name="ban" size={14} />
                            </button>
                          )}
                          <button
                            className="icon-btn"
                            title="Delete"
                            style={{ color: "var(--error)" }}
                            onClick={() => onDelete(c)}
                          >
                            <Icon name="trash-2" size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && resp.total > 0 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 16, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <Icon name="chevron-left" size={14} /> Prev
          </button>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Page {page} of {totalPages}
          </span>
          <button className="btn btn-ghost btn-sm" disabled={!resp.hasMore} onClick={() => setPage((p) => p + 1)}>
            Next <Icon name="chevron-right" size={14} />
          </button>
        </div>
      )}
    </section>
  );
}

export default function AdminCompanies() {
  return (
    <AdminPageBoundary>
      <CompaniesInner />
    </AdminPageBoundary>
  );
}
